import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listEstimates, deleteEstimate, verifyEstimatorPassword } from "@/lib/estimator.functions";
import { getStoredPassword, setStoredPassword } from "@/lib/estimator-session";
import { ArrowLeft, ArrowRight, Trash2, Eye, FolderOpen, Lock, Search, Image as ImageIcon } from "lucide-react";

export const Route = createFileRoute("/estimator_/saved")({
  head: () => ({
    meta: [
      { title: "Saved Estimates | Apex AV" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SavedEstimatesPage,
});

type EstimateRow = {
  id: string;
  name: string;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  client_address: string | null;
  total: number;
  created_at: string;
  updated_at: string;
  blueprint_page_count: number | null;
};

function SavedEstimatesPage() {
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && getStoredPassword()) setUnlocked(true);
  }, []);
  return unlocked ? <SavedList /> : <PasswordGate onUnlock={() => setUnlocked(true)} />;
}

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const verify = useServerFn(verifyEstimatorPassword);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await verify({ data: { password } });
      if (res.ok) {
        setStoredPassword(password);
        onUnlock();
      } else {
        setError("Incorrect password.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="container mx-auto px-6 py-24 flex items-center justify-center min-h-[70vh]">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl border border-border bg-card p-10 shadow-card">
        <div className="h-12 w-12 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow">
          <Lock className="h-6 w-6 text-brand-foreground" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold">Saved estimates</h1>
        <p className="mt-2 text-sm text-muted-foreground">Enter the estimator password to view saved estimates.</p>
        <label className="block mt-8 text-sm font-medium">Password</label>
        <input
          type="password"
          aria-label="Password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full rounded-md border border-border bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand"
        />
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow disabled:opacity-50"
        >
          {loading ? "Verifying…" : "Unlock"} <ArrowRight className="h-4 w-4" />
        </button>
      </form>
    </section>
  );
}

function SavedList() {
  const listFn = useServerFn(listEstimates);
  const deleteFn = useServerFn(deleteEstimate);
  const [rows, setRows] = useState<EstimateRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const fmt = (n: number) =>
    Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  async function load() {
    const pw = getStoredPassword();
    if (!pw) return;
    try {
      const { estimates } = await listFn({ data: { password: pw } });
      setRows(estimates as EstimateRow[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load.");
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleDelete(id: string) {
    const pw = getStoredPassword();
    if (!pw) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteFn({ data: { password: pw, id } });
      setRows((prev) => (prev ?? []).filter((r) => r.id !== id));
      setConfirmId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete.");
    } finally {
      setDeletingId(null);
    }
  }

  const filtered = useMemo(() => {
    if (!rows) return null;
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.client_name, r.client_email, r.client_phone, r.client_address]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [rows, query]);

  return (
    <section className="container mx-auto px-6 pt-16 pb-32">
      <Link
        to="/estimator"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to estimator
      </Link>
      <div className="mt-6 flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow">
          <FolderOpen className="h-5 w-5 text-brand-foreground" />
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold">Saved estimates</h1>
      </div>

      {error && <p className="mt-6 text-sm text-destructive">{error}</p>}

      <div className="mt-8 relative max-w-xl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, client, email, phone, or address…"
          className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
        />
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card shadow-card overflow-hidden">
        {filtered === null ? (
          <p className="p-8 text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-muted-foreground">
              {rows && rows.length > 0 ? "No estimates match your search." : "No saved estimates yet."}
            </p>
            {(!rows || rows.length === 0) && (
              <Link
                to="/estimator"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow"
              >
                Create your first estimate
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Client</th>
                  <th className="px-6 py-3 font-medium">Contact</th>
                  <th className="px-6 py-3 font-medium text-right">Total</th>
                  <th className="px-6 py-3 font-medium">Last updated</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="px-6 py-4 font-medium">{r.name}</td>
                    <td className="px-6 py-4 text-muted-foreground">
                      <div>{r.client_name || "—"}</div>
                      {r.client_address && (
                        <div className="text-xs mt-0.5">{r.client_address}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground text-xs">
                      {r.client_email && <div>{r.client_email}</div>}
                      {r.client_phone && <div>{r.client_phone}</div>}
                      {!r.client_email && !r.client_phone && <span>—</span>}
                    </td>
                    <td className="px-6 py-4 text-right font-display font-bold">{fmt(r.total)}</td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {new Date(r.updated_at).toLocaleDateString("en-US", {
                        year: "numeric", month: "short", day: "numeric",
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to="/estimator"
                          search={{ id: r.id }}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                        >
                          <Eye className="h-3.5 w-3.5" /> View
                        </Link>
                        {Number(r.blueprint_page_count ?? 0) > 0 && (
                          <Link
                            to="/blueprint-counter"
                            search={{ estimateId: r.id }}
                            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
                            title={`${r.blueprint_page_count} blueprint page${Number(r.blueprint_page_count) === 1 ? "" : "s"} — open in counter to edit`}
                          >
                            <ImageIcon className="h-3.5 w-3.5" /> Blueprint
                          </Link>
                        )}
                        {confirmId === r.id ? (
                          <>
                            <button
                              onClick={() => handleDelete(r.id)}
                              disabled={deletingId === r.id}
                              className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> {deletingId === r.id ? "Deleting…" : "Confirm"}
                            </button>
                            <button
                              onClick={() => setConfirmId(null)}
                              disabled={deletingId === r.id}
                              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setConfirmId(r.id)}
                            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
